import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'
import { TracepointRule } from '../../../domain/TracepointRule'
import { ApplicationState } from '../../../redux/ApplicationState'
import { terminateTracepoint, updateTracepoint } from '../../../redux/V2/TracepointRule/actions'
import { TracepointFormWrapper } from './tracepoint-form-wrapper'
import TracepointFormFields, { TracepointFormState, TracepointRuleFilterThresholdType } from './tracepoint-form-fields'
import TracepointFormErrors from './tracepoint-form-errors'

interface OwnProps {
    onExit: () => void
    tracepoint: TracepointRule<moment.Moment>
    allowedDurations: number[]
    showNotifyOnFirstSlow: boolean
    annotationDefaults?: { [key: string]: string | number }
    error?: string | null
}

interface StateProps {
    customAnnotations: string[]
}

interface DispatchProps {
    updateTracepoint: (tracepoint: TracepointRule<moment.Moment>, state: any) => void
    terminateTracepoint: (tracepoint: TracepointRule<moment.Moment>) => void
}

type Props = StateProps & DispatchProps & OwnProps

type State = {
    error?: string | null
    processing: boolean
}

class EditTracepointForm extends React.Component<Props, State> {
    public static defaultProps = {
        annotationDefaults: {},
        showNotifyOnFirstSlow: false,
    }

    public constructor(props: Props) {
        super(props)

        this.state = {
            error: props.error,
            processing: false,
        }
    }

    public render = (): React.ReactNode => {
        const { allowedDurations, onExit, annotationDefaults, showNotifyOnFirstSlow, error, tracepoint } = this.props
        const isActive = tracepoint.activeUntil >= moment()

        const initalFormState = {
            annotations: tracepoint.annotations,
            duration: tracepoint.duration,
            filterThresholdMs: tracepoint.filterThresholdMs || 0,
            filterThresholdMemoryMb: tracepoint.filterThresholdMemoryMb || 0,
            notifyOnCompletion: tracepoint.notifyOnCompletion,
            notifyOnFirst: tracepoint.notifyOnFirst,
            notifyOnFirstSlow: tracepoint.notifyOnFirstSlow,
            notifyOnFirstSlowThreshold: tracepoint.notifyOnFirstSlowThreshold || 0,
            notificationIntegrations: tracepoint.notificationIntegrations,
            notificationPersonalEmail: tracepoint.notificationPersonalEmail,
            enableCallgraph: tracepoint.enableCallgraph,
            callgraphDuration: tracepoint.callgraphDuration,
            filterThresholdType: tracepoint.filterThresholdMemoryMb
                ? TracepointRuleFilterThresholdType.MEMORY_MB
                : TracepointRuleFilterThresholdType.TIME_MS,
        }

        return (
            <TracepointFormWrapper>
                <TracepointFormErrors error={error} />
                <TracepointFormFields
                    allowedDurations={allowedDurations}
                    annotationDefaults={annotationDefaults}
                    onSubmit={this.handleSubmit}
                    transaction={tracepoint.transaction}
                    showNotifyOnFirstSlow={showNotifyOnFirstSlow}
                    initialState={initalFormState}
                    renderFormActions={isValid => (
                        <>
                            <button className="btn btn-primary" disabled={!isValid}>
                                save
                                {this.state.processing && <i className="fa fa-spinner fa-spin" />}
                            </button>{' '}
                            <button className="btn btn-secondary" onClick={onExit}>
                                cancel
                            </button>
                            {isActive && (
                                <button
                                    className="btn btn-danger pull-right"
                                    onClick={e => {
                                        e.preventDefault()
                                        this.terminate()
                                    }}
                                    title="stop tracepoint boosting immediately"
                                >
                                    <i className="fa fa-trash" />
                                </button>
                            )}
                        </>
                    )}
                />
            </TracepointFormWrapper>
        )
    }

    private handleSubmit = async (state: TracepointFormState) => {
        try {
            this.setState({ processing: true })
            await this.props.updateTracepoint(this.props.tracepoint, state)
            this.props.onExit()
        } catch (error) {
            this.setState({ error: error.message })
        } finally {
            this.setState({ processing: false })
        }
    }

    private terminate = async () => {
        try {
            this.setState({ processing: true })
            await this.props.terminateTracepoint(this.props.tracepoint)
            this.props.onExit()
        } catch (error) {
            this.setState({ error: error.message })
        } finally {
            this.setState({ processing: false })
        }
    }
}

export default connect<StateProps, DispatchProps>(
    (globalState: ApplicationState, props: Props): StateProps => {
        return {
            customAnnotations: globalState.tidewaysContext.customAnnotations || [],
        }
    },
    {
        updateTracepoint,
        terminateTracepoint,
    },
)(EditTracepointForm)
